import { modeZoom } from "@golazo/shared";

/**
 * Cuanto campo quieres ver.
 *
 * El acercamiento del juego lo fijaba `modeZoom()` a partir del tamaño del
 * jugador: un numero por modo, igual para todo el mundo. Y es un buen valor por
 * defecto, pero no hay uno bueno para todos — en un movil de 5 pulgadas quieres
 * ver los pies, y en un monitor de 27 quieres ver la jugada entera. Esto es un
 * multiplicador ENCIMA de ese valor, no un sustituto: el equilibrio por modo se
 * conserva y cada uno lo estira a su gusto.
 *
 * **Es solo camara.** No toca la simulacion, asi que dos jugadores del mismo
 * partido online pueden tener acercamientos distintos sin que los partidos se
 * separen: el lockstep no ve esto ni puede verlo. Meterlo en la simulacion
 * seria un desincronizador de manual.
 */

/**
 * El paso de zoom, identificado por su propio multiplicador.
 *
 * Antes eran nombres (AMPLIA / NORMAL / CERCA) y no decian nada: "amplia"
 * respecto a que, y cuanto mas que "normal". Un numero se compara solo — de 1,0
 * a 1,6 sabes exactamente cuanto te acercas — y de paso deja añadir pasos sin
 * inventar mas adjetivos.
 */
/**
 * Cuatro pasos, y el primero enseña EL CAMPO ENTERO.
 *
 * Eran seis y sobraban: un ciclo de seis obliga a dar cinco toques para volver
 * a donde estabas, y entre 1,2x y 1,4x nadie distingue nada jugando.
 *
 * El paso "CAMPO" es distinto de los demas y por eso no lleva numero: no es un
 * multiplicador, es "que quepa todo". Existe para quien prefiere ver la jugada
 * completa en vez de ir mirando el minimapa — con el campo entero delante, el
 * minimapa deja de hacer falta.
 */
export type CameraMode = "fit" | "1" | "1.4" | "1.8";

interface CameraModeSpec {
  id: CameraMode;
  /** Multiplicador sobre `modeZoom()`. Se ignora si `fit` es true. */
  factor: number;
  /**
   * "Que quepa el campo entero", en vez de un acercamiento.
   *
   * No se puede expresar como multiplicador porque `modeZoom()` cambia con el
   * modo (el jugador de 11v11 es mas pequeño que el de 2v2), asi que el factor
   * que encaja el campo seria distinto en cada uno. Se resuelve en
   * `computePlayViewport`, que es donde se conoce la pantalla.
   */
  fit?: boolean;
  /** Lo que se lee en el boton. */
  label: string;
}

const SPECS: readonly CameraModeSpec[] = [
  // Sin etiqueta fija: la suya se calcula, ver `cameraModeLabel`.
  { id: "fit", factor: 1, fit: true, label: "" },
  { id: "1", factor: 1, label: "1×" },
  { id: "1.4", factor: 1.4, label: "1.4×" },
  { id: "1.8", factor: 1.8, label: "1.8×" },
];

const STORAGE_KEY = "fynta.camera";
const DEFAULT_MODE: CameraMode = "1";

function specOf(mode: CameraMode): CameraModeSpec {
  return SPECS.find((s) => s.id === mode) ?? SPECS[1]!;
}

function isCameraMode(value: string | null): value is CameraMode {
  return SPECS.some((s) => s.id === value);
}

let current: CameraMode | null = null;
/**
 * Un modo impuesto para el partido en curso, sin tocar la preferencia guardada
 * (dos en el mismo ordenador: tienen que verse los dos). `null` lo quita.
 */
let override: CameraMode | null = null;

export function setCameraOverride(mode: CameraMode | null): void {
  override = mode;
}

export function cameraMode(): CameraMode {
  if (override !== null) return override;
  if (current !== null) return current;
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    current = isCameraMode(saved) ? saved : DEFAULT_MODE;
  } catch {
    // Ventana privada o almacenamiento bloqueado: se juega con el de serie.
    current = DEFAULT_MODE;
  }
  return current;
}

/** El multiplicador que aplica `computePlayViewport`. */
export function cameraZoomFactor(): number {
  return specOf(cameraMode()).factor;
}

/** ¿El modo actual pide que quepa el campo entero? */
export function cameraFitsField(): boolean {
  return specOf(cameraMode()).fit === true;
}

/**
 * Lo que se lee en el boton.
 *
 * El paso que encaja el campo entero tambien lleva numero, pero el suyo se
 * CALCULA en vez de escribirse: no hay un multiplicador fijo que haga caber el
 * campo, porque `modeZoom()` cambia con el modo (el jugador de 11v11 se dibuja
 * mas pequeño que el de 2v2, asi que el juego ya viene mas alejado). Poner un
 * "0.7x" a mano seria mentir en la mitad de los modos.
 *
 * Sale de la propia definicion: el encaje es `fitScale`, el paso normal es
 * `fitScale * modeZoom()`, luego el cociente es `1 / modeZoom()`.
 */
export function cameraModeLabel(mode: CameraMode = cameraMode()): string {
  const spec = specOf(mode);
  if (!spec.fit) return spec.label;
  const ratio = 1 / Math.max(0.01, modeZoom());
  return `${ratio.toFixed(1)}×`;
}

const listeners = new Set<() => void>();

/** Avisa a quien tenga que recalcular el encuadre. Devuelve como desapuntarse. */
export function onCameraModeChange(fn: () => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

export function setCameraMode(mode: CameraMode): void {
  if (cameraMode() === mode) return;
  current = mode;
  try {
    localStorage.setItem(STORAGE_KEY, mode);
  } catch {
    // Se pierde al recargar, pero la partida en curso la respeta.
  }
  for (const fn of listeners) fn();
}

/** Siguiente paso del ciclo. Es lo que hace el boton del partido. */
export function cycleCameraMode(): CameraMode {
  const i = SPECS.findIndex((s) => s.id === cameraMode());
  const next = SPECS[(i + 1) % SPECS.length]!;
  // Con un modo impuesto, el boton cambia el del partido, no la preferencia.
  if (override !== null) override = next.id;
  else setCameraMode(next.id);
  return next.id;
}

export { SPECS as CAMERA_MODES };
